// Sortable table for list views. Sorting and paging happen in the browser: the lists
// are a few thousand rows at most.

import { ArrowDown, ArrowUp } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { cx, Empty } from './ui';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Value to sort by; omit to make the column unsortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  className?: string;
}

const PAGE = 100;

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  onRowClick,
  initialSort,
  empty = 'Nothing here yet.',
  selected,
  onToggleSelect,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  empty?: ReactNode;
  /** Optional row checkboxes for bulk actions. */
  selected?: Set<string>;
  onToggleSelect?: (keys: string[], on: boolean) => void;
}) {
  const [sort, setSort] = useState(initialSort ?? null);
  const [limit, setLimit] = useState(PAGE);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      if (va === vb) return 0;
      if (va === null || va === undefined || va === '') return 1; // blanks last
      if (vb === null || vb === undefined || vb === '') return -1;
      return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))) * dir;
    });
  }, [rows, sort, columns]);

  const visible = sorted.slice(0, limit);
  const allVisibleSelected = selected && visible.length > 0 && visible.every((r) => selected.has(rowKey(r)));

  if (rows.length === 0) return <Empty>{empty}</Empty>;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
          <tr>
            {selected && onToggleSelect && (
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!allVisibleSelected}
                  onChange={(e) => onToggleSelect(visible.map(rowKey), e.target.checked)}
                  aria-label="Select all shown"
                />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} className={cx('whitespace-nowrap px-3 py-2', c.className)}>
                {c.sortValue ? (
                  <button
                    className="inline-flex items-center gap-1 hover:text-slate-800"
                    onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'asc' }))}
                  >
                    {c.header}
                    {sort?.key === c.key && (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {visible.map((row) => {
            const key = rowKey(row);
            return (
              <tr key={key} className={cx(onRowClick && 'cursor-pointer hover:bg-indigo-50/40')} onClick={() => onRowClick?.(row)}>
                {selected && onToggleSelect && (
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(key)} onChange={(e) => onToggleSelect([key], e.target.checked)} aria-label="Select row" />
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className={cx('px-3 py-2 align-top text-slate-700', c.className)}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
        <span>
          Showing {visible.length} of {rows.length}
        </span>
        {limit < rows.length && (
          <button className="font-medium text-indigo-600 hover:underline" onClick={() => setLimit((l) => l + PAGE * 5)}>
            Show more
          </button>
        )}
      </div>
    </div>
  );
}
